import {LayerData, SelectableElement, assetTypeSchema} from "@app/common/components/viewers/configurator/extensions/bd-cloud/types"
import {MeshNode, isNamedNode, TemplateNode} from "@cm/template-nodes"
import {Mattress, MattressCut} from "@cm/mattress-nodes"
import {ExtensionContext} from "@app/common/components/viewers/configurator/extensions/configurator-api-extension"
import {filterDescriptorsBySelection} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/descriptors"
import {
    getAccessoryBaseId,
    getAccessoryTypeDescriptor,
    getPullerBaseIdFromPullerAccessoryType,
} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/accessories"
import {getHandleBaseId} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/handles"
import {getSelectedMaterialSlot} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/material-slots"
import {
    createPipingOrTapeParametric,
    createMeshWithInputs,
    createZipper,
    createFabricLayer,
    createHandle,
    createPuller,
} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/create-element"

function getAssetTypeFromName(element: TemplateNode): SelectableElement["type"] | undefined {
    if (isNamedNode(element)) {
        const lowerCaseName = element.parameters.name.toLowerCase()
        if (lowerCaseName.includes("piping")) return "Piping"
        if (lowerCaseName.includes("handle")) return "Handle"
        if (lowerCaseName.includes("zipper")) return "Zipper"
        if (lowerCaseName.includes("tape")) return "Tape"
        if (lowerCaseName.includes("puller")) return "Puller"
    }

    if (element instanceof MeshNode) return "FabricLayer"

    return undefined
}

function capitalizeFirst(s: string) {
    return s.charAt(0).toUpperCase() + s.slice(1)
}

export async function getSelectedElementType(context: ExtensionContext, layers: LayerData[]): Promise<SelectableElement | undefined> {
    const objectPathParts = context.sceneManagerService.$selectedObjectPathParts()

    const parametricMattress = objectPathParts.some((parts) => parts.path.some((node) => node instanceof Mattress))

    const getMattressCut = (): MattressCut | undefined => {
        for (const path of objectPathParts) {
            const candidate = path.path.at(-1)
            if (candidate instanceof MattressCut) return candidate
        }

        return undefined
    }

    const filteredDescriptors = filterDescriptorsBySelection(context)
    if (parametricMattress) {
        const accessoryTypeDescriptor = getAccessoryTypeDescriptor(filteredDescriptors)

        if (accessoryTypeDescriptor) {
            const typeValue = accessoryTypeDescriptor.props.value
            if (!typeValue) throw new Error("Accessory type descriptor has no value")

            const parsed = assetTypeSchema.safeParse(capitalizeFirst(typeValue))
            if (!parsed.success) {
                context.logger.error("Failed to parse accessory type", {value: accessoryTypeDescriptor.props.value})
                return undefined
            }
            if (parsed.data === "Piping" || parsed.data === "Tape") {
                return createPipingOrTapeParametric(
                    parsed.data,
                    getAccessoryBaseId(accessoryTypeDescriptor.props.id),
                    context.sceneManagerService.$descriptorList(),
                    context,
                )
            }
            if (parsed.data === "Zipper") {
                return createZipper(getAccessoryBaseId(accessoryTypeDescriptor.props.id), context.sceneManagerService.$descriptorList(), context)
            }
            if (parsed.data === "Puller") {
                const pullerBaseId = getPullerBaseIdFromPullerAccessoryType(accessoryTypeDescriptor.props.id)
                const puller = await createPuller(pullerBaseId, context.sceneManagerService.$descriptorList(), context)
                return {...puller, selectedMaterialSlot: getSelectedMaterialSlot(pullerBaseId, context)}
            }
            if (parsed.data === "Handle") {
                const handleBaseId = getHandleBaseId(accessoryTypeDescriptor.props.id)
                const handle = await createHandle(handleBaseId, context.sceneManagerService.$descriptorList(), context)
                return {...handle, selectedMaterialSlot: getSelectedMaterialSlot(handleBaseId, context)}
            }
        }

        const mattressCut = getMattressCut()
        if (mattressCut && mattressCut instanceof MattressCut) {
            return createFabricLayer(mattressCut.parameters.name, filteredDescriptors, context.sceneManagerService.$descriptorList(), layers, context)
        }
    } else {
        const element = objectPathParts[0]?.path?.at(-1)
        if (!element) return undefined
        const assetType = getAssetTypeFromName(element)
        if (assetType === "Piping" || assetType === "Tape" || assetType === "Handle" || assetType === "FabricLayer") {
            return createMeshWithInputs(assetType, element, filteredDescriptors, context)
        }
    }

    return undefined
}
